'use client';

import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type PasswordInputProps = Omit<React.ComponentProps<'input'>, 'type'> & {
  /** Show a "Bloq Mayús activado" hint while Caps Lock is on. Default true. */
  capsLockWarning?: boolean;
  /** Base id for test/E2E hooks: input gets `testId`, caps hint `${testId}-caps`. */
  testId?: string;
  /** Optional controlled visibility. When omitted, the toggle is self-managed. */
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
};

/**
 * Password field with a built-in show/hide toggle and an optional Caps-Lock
 * hint. Thin wrapper around the shadcn `Input` — forwards every input prop
 * (including the `ref` that React Hook Form's `register` passes) and owns the
 * `type`. The reveal button is `type="button"` so it never submits the form.
 */
function PasswordInput({
  className,
  disabled,
  capsLockWarning = true,
  testId,
  visible,
  onVisibleChange,
  onKeyUp,
  onKeyDown,
  onBlur,
  ...props
}: PasswordInputProps) {
  const [internalShow, setInternalShow] = React.useState(false);
  const [capsOn, setCapsOn] = React.useState(false);

  // Controlled when `visible` is provided, otherwise self-managed.
  const show = visible ?? internalShow;
  const toggleShow = () => {
    const next = !show;
    if (visible === undefined) setInternalShow(next);
    onVisibleChange?.(next);
  };

  const syncCaps = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!capsLockWarning) return;
    const native = e.nativeEvent;
    if (typeof native?.getModifierState === 'function') {
      setCapsOn(native.getModifierState('CapsLock'));
    }
  };

  return (
    <div>
      <div className="relative">
        <Input
          type={show ? 'text' : 'password'}
          disabled={disabled}
          data-testid={testId}
          className={cn('pr-10', className)}
          onKeyUp={(e) => {
            syncCaps(e);
            onKeyUp?.(e);
          }}
          onKeyDown={(e) => {
            syncCaps(e);
            onKeyDown?.(e);
          }}
          onBlur={(e) => {
            setCapsOn(false);
            onBlur?.(e);
          }}
          {...props}
        />
        <button
          type="button"
          onClick={toggleShow}
          disabled={disabled}
          tabIndex={-1}
          aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          aria-pressed={show}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 transition-colors hover:text-gray-600 disabled:pointer-events-none disabled:opacity-50"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {capsLockWarning && capsOn && (
        <p
          data-testid={testId ? `${testId}-caps` : undefined}
          aria-live="polite"
          className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500"
        >
          <span aria-hidden="true">⚠</span> Bloq Mayús activado
        </p>
      )}
    </div>
  );
}

export { PasswordInput };
